import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import LodgeHeaderBar from "./lodgeHeaderBar";
import UpNext from "./upNextComp";
import ExploreCard from "./exploreComponent";
import SocialHeader from "./SocialFeedHeader";
import LodgeLoadingWrapper from "./LodgeLoadingWrapper";
import NoTripsComponent from "./NoTrips";
import UserActiveTripDropDown from "./userActiveTripDropDown";
import { BookmarkIcon } from "@heroicons/react/20/solid";
import tripService from "@/services/tripService";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { TripX } from "@/types/trip";
import useLodgeStore from "@/stores/lodge/lodgeStore";

const LodgeLayout: React.FC = () => {
  const { selectedCurrency, setIsCurrencyLoading } = useCurrencyStore();
  const [selectedTrip, setSelectedTrip] = useState<TripX | null>(null);
  const setTrips = useLodgeStore((state) => state.setTrips);
  const initializeTripTodos = useLodgeStore(
    (state) => state.initializeTripTodos
  );

  const {
    data: tripData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["trips", selectedCurrency.code],
    queryFn: () =>
      tripService.getTrips({
        todos: true,
        currency: selectedCurrency.code,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    placeholderData: (prev) => prev, // Use previous data while fetching new data
  });

  const effectDeps = useMemo(
    () => ({ tripData, setTrips, initializeTripTodos, selectedTrip }),
    [tripData, setTrips, initializeTripTodos, selectedTrip]
  );

  useEffect(() => {
    if (effectDeps.tripData) {
      effectDeps.setTrips(effectDeps.tripData);
      effectDeps.initializeTripTodos(effectDeps.tripData);

      if (effectDeps.selectedTrip) {
        const updatedTrip = effectDeps.tripData.find(
          (trip) => trip.id === effectDeps.selectedTrip?.id
        );
        if (updatedTrip && updatedTrip !== effectDeps.selectedTrip) {
          setSelectedTrip(updatedTrip);
        }
      }
      setIsCurrencyLoading(false);
    }
  }, [effectDeps, setIsCurrencyLoading]);

  // Only show loading on initial load, not during background refetches (like currency changes)
  const showLoading = isLoading && !isFetching;

  const MainContent = useMemo(() => {
    if (showLoading) return null;

    if (!tripData?.length) return <NoTripsComponent />;

    return (
      <div className="flex flex-col h-full w-full">
        <div className="flex flex-row w-full justify-between items-center min-h-16">
          <div className="flex flex-row space-x-2">
            <button
              className={`flex flex-row items-center space-x-2 px-3 py-2 text-sm font-medium rounded-full transition ease-in-out duration-300 bg-indigo-800 text-white`}
            >
              <BookmarkIcon className="w-4 h-4" />
              <p>What's Next</p>
            </button>
            <UserActiveTripDropDown
              trips={tripData}
              selectedTrip={selectedTrip}
              onTripSelect={setSelectedTrip}
            />
          </div>
        </div>
        <div className="flex flex-col w-full h-[calc(100vh-130px)] space-y-2.5">
          <UpNext
            trips={tripData}
            selectedTrip={selectedTrip}
            setSelectedTrip={setSelectedTrip}
          />
          <ExploreCard selectedTrip={selectedTrip} />
        </div>
      </div>
    );
  }, [showLoading, tripData, selectedTrip, setSelectedTrip]);

  return (
    <LodgeLoadingWrapper loadingStates={{ initialData: showLoading }}>
      <div className="flex flex-col h-full w-full">
        <LodgeHeaderBar />
        <div className="flex flex-row space-x-2 w-full h-[calc(100vh-60px)]">
          <SocialHeader />
          {MainContent}
        </div>
      </div>
    </LodgeLoadingWrapper>
  );
};

export default React.memo(LodgeLayout);
